// Columns side by side per category, on recharts 3 (ADR 0002): two or three
// series compared inside each category (actual against budget, this period
// against the last). ColumnChart's sibling for more than one series, with
// TrendChart's props, so a page swaps the mark and keeps its data. The
// legend names the series; the figure, the summary sentence, and the hidden
// table carry every value in words, so nothing rests on the bar colours.
import { Bar, BarChart, CartesianGrid, Tooltip, XAxis, YAxis } from "recharts";
import { ChartFigure, ChartTooltip, defaultFormat, SERIES_COLORS, type ChartBaseProps, type ChartHeight } from "./frame";
import { CATEGORY_TICK_MARGIN, CategoryFrame, categoryAxisProps, planCategoryAxis, tickAxisWidth, type TrendDatum, type TrendSeries } from "./Cartesian";

/** Shared by the column charts of this module: the SVG fills its canvas, ticks are --text-n1. */
export const CHART_FILL = { width: "100%", height: "100%" } as const;
export const CHART_TICK = { fontSize: 13 } as const;

/** Series the grouped columns are measured for. More still draw, each bar thinner. */
export const GROUPED_SERIES_MAX = 3;

/** The value of one series in a datum, as a number; a missing or non numeric cell is 0. */
export function seriesValue(datum: TrendDatum, key: string): number {
  return Number(datum[key]) || 0;
}

/** The colour of the series at this position, in SERIES_COLORS order. */
export function seriesColor(index: number): string {
  return SERIES_COLORS[index % SERIES_COLORS.length] as string;
}

/**
 * The legend key of a series drawn as a line: the mark itself in small, a
 * rule with a ring on it, in the series colour. Beside the square key of a
 * filled series it tells the two apart by shape, never by hue alone.
 * `dashed` matches a line that is drawn dashed.
 */
export function LineKey({ color, dashed = false }: { color: string; dashed?: boolean }) {
  return (
    <span className="con-swatch" data-shape="line" style={{ color }}>
      <svg viewBox="0 0 20 12" focusable="false" role="presentation">
        <line x1={1} y1={6} x2={19} y2={6} strokeWidth={2} strokeLinecap={dashed ? undefined : "round"} strokeDasharray={dashed ? "3 2" : undefined} />
        <circle cx={10} cy={6} r={3} strokeWidth={2} />
      </svg>
    </span>
  );
}

export interface GroupedColumnChartProps extends Omit<ChartBaseProps, "valueLabel"> {
  /**
   * Categories in their natural order; `label` is the x value. The axis is
   * the one ColumnChart draws: 8 characters or less on one line, a longer
   * label wrapped onto two (columnLabelsFit says whether a set prints at
   * every width).
   */
  data: TrendDatum[];
  /**
   * 2 or 3 series (GROUPED_SERIES_MAX), drawn side by side in each category
   * in the order given and coloured from SERIES_COLORS in that order.
   */
  series: TrendSeries[];
  height?: ChartHeight;
}

/**
 * Two or three columns per category on one y scale, with the kit legend.
 * The plot stretches to the height its card is given.
 */
export function GroupedColumnChart({ label, summary, categoryLabel, format = defaultFormat, data, series, height = "md" }: GroupedColumnChartProps) {
  const values = data.flatMap((d) => series.map((s) => seriesValue(d, s.key)));
  const labels = data.map((d) => d.label);
  const yWidth = tickAxisWidth(values, format);
  const plan = planCategoryAxis(labels, yWidth);
  const table = {
    caption: label,
    head: [categoryLabel, ...series.map((s) => s.label)],
    rows: data.map((d) => [d.label, ...series.map((s) => format(seriesValue(d, s.key)))]),
  };
  const legend = (
    <ul className="con-legend" aria-hidden="true">
      {series.map((s, i) => (
        <li key={s.key} className="con-legend-item">
          <span className="con-swatch" style={{ background: seriesColor(i) }} />
          <span className="con-legend-label">{s.label}</span>
        </li>
      ))}
    </ul>
  );
  return (
    <ChartFigure label={label} summary={summary} kind="grouped" height={height} table={table} legend={legend}>
      <CategoryFrame plan={plan} labels={labels}>
        <BarChart responsive style={CHART_FILL} data={data} margin={{ top: 8, right: 4, bottom: 0, left: 0 }} barGap={2} barCategoryGap="20%" accessibilityLayer={false}>
          <CartesianGrid vertical={false} />
          {plan.mode === "plain" ? (
            <XAxis dataKey="label" tickLine={false} tick={CHART_TICK} interval="preserveStartEnd" minTickGap={4} tickMargin={CATEGORY_TICK_MARGIN} height={28} />
          ) : (
            <XAxis {...categoryAxisProps(plan)} />
          )}
          <YAxis tickLine={false} axisLine={false} tick={CHART_TICK} tickCount={4} allowDecimals={false} width={yWidth} tickFormatter={(v: number) => format(v)} />
          <Tooltip cursor={{ fill: "var(--color-layer-1)" }} content={<ChartTooltip format={format} />} isAnimationActive={false} />
          {series.map((s, i) => (
            <Bar key={s.key} dataKey={s.key} name={s.label} fill={seriesColor(i)} radius={[4, 4, 0, 0]} maxBarSize={48} isAnimationActive={false} />
          ))}
        </BarChart>
      </CategoryFrame>
    </ChartFigure>
  );
}
